import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, CircularProgress, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import moment from 'moment-jalaali';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';

// Icons
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GavelIcon from '@mui/icons-material/Gavel';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InventoryIcon from '@mui/icons-material/Inventory';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ChatIcon from '@mui/icons-material/Chat';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PolicyIcon from '@mui/icons-material/Policy';

import UnifiedResolutionDialog, {
  DisputeData,
  ActionType,
  ActionPayload
} from '../components/arbitration/modals/UnifiedResolutionDialog';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';

import './DisputeCenter.css';

// Types
type DisputeType = 'order' | 'order_item' | 'inspection';
type DisputeStatus = 'pending_admin_approval' | 'approved' | 'rejected' | 'investigating' | 'resolved' | 'closed';
type Priority = 'low' | 'medium' | 'high' | 'urgent';

interface DisputeParty {
  id: string;
  name: string;
  email?: string;
  role: 'buyer' | 'seller' | 'party_a' | 'party_b';
}

interface UnifiedDispute {
  id: string;
  disputeNumber: string;
  type: DisputeType;
  status: DisputeStatus;
  reason: string;
  reasonLabel: string;
  description: string;
  references: {
    orderId?: string;
    orderNumber?: string;
    orderItemId?: string;
    productTitle?: string;
    productId?: string;
    storeId?: string;
    storeName?: string;
  };
  parties: {
    initiator: DisputeParty;
    respondent?: DisputeParty;
  };
  financial?: {
    amount: number;
    shippingCost?: number;
    refundableAmount: number;
  };
  chatId?: string;
  assignedAdminId?: string;
  assignedAdminName?: string;
  createdAt: string;
  updatedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
  resolvedAt?: string;
  adminInfo?: {
    approvedBy?: string;
    approvedByName?: string;
    rejectedBy?: string;
    rejectedByName?: string;
    resolvedBy?: string;
    resolvedByName?: string;
    rejectReason?: string;
    resolution?: string;
    resolutionType?: 'buyer_favor' | 'seller_favor' | 'mutual_agreement';
    refundAmount?: number;
    refundReason?: string;
    penalties?: Array<{ userId: string; userName?: string; points: number }>;
    restrictions?: Array<{ userId: string; userName?: string; restrict: boolean }>;
    tags?: string[];
  };
  priority: Priority;
}

interface DisputeStats {
  total: number;
  pending: number;
  approved: number;
  investigating: number;
  resolved: number;
  rejected: number;
  closed: number;
  byType: {
    order: number;
    order_item: number;
    inspection: number;
  };
}

// Helpers
const formatDate = (d: string | undefined) => d ? moment(d).format('jYYYY/jMM/jDD - HH:mm') : '-';
const formatRelativeTime = (d: string | undefined) => {
  if (!d) return '-';
  const date = new Date(d);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 60) return `${diffMins} دقیقه پیش`;
  if (diffHours < 24) return `${diffHours} ساعت پیش`;
  if (diffDays < 7) return `${diffDays} روز پیش`;
  return formatDate(d);
};
const formatPrice = (p: number) => new Intl.NumberFormat('fa-IR').format(p);

const statusLabels: Record<string, string> = {
  pending_admin_approval: 'در انتظار تأیید',
  approved: 'تأیید شده',
  investigating: 'در حال بررسی',
  resolved: 'حل شده',
  rejected: 'رد شده',
  closed: 'بسته شده'
};

const typeLabels: Record<string, string> = {
  order: 'سفارش',
  order_item: 'محصول',
  inspection: 'بازرسی'
};

const priorityLabels: Record<string, string> = {
  urgent: 'فوری',
  high: 'بالا',
  medium: 'متوسط',
  low: 'پایین'
};

const DisputeCenter: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [disputes, setDisputes] = useState<UnifiedDispute[]>([]);
  const [stats, setStats] = useState<DisputeStats | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | DisputeType>('all');
  const [statusFilter, setStatusFilter] = useState<DisputeStatus | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<Priority | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Dialogs
  const [selectedDispute, setSelectedDispute] = useState<UnifiedDispute | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [unifiedOpen, setUnifiedOpen] = useState(false);
  const [unifiedError, setUnifiedError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Reassign dispute chat
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignDispute, setReassignDispute] = useState<UnifiedDispute | null>(null);
  const [eligibleAdmins, setEligibleAdmins] = useState<Array<{ _id: string; name: string; email?: string; status?: string; isSuperAdmin?: boolean; currentLoad?: number; overloaded?: boolean }>>([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [reassigning, setReassigning] = useState(false);

  // Fetch
  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit, sortBy: 'createdAt', sortOrder: 'desc' };
      if (activeTab !== 'all') params.type = activeTab;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (searchQuery) params.search = searchQuery;

      const res = await apiClient.get('/admin/unified-disputes', { params });
      if (res.data.status === 'success') {
        setDisputes(res.data.data.disputes);
        setTotalPages(res.data.data.totalPages);
        setTotal(res.data.data.total);
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar('خطا در دریافت اختلافات', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [activeTab, statusFilter, priorityFilter, searchQuery, page, enqueueSnackbar]);

  const fetchStats = async () => {
    try {
      const res = await apiClient.get('/admin/unified-disputes/stats');
      if (res.data.status === 'success') setStats(res.data.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchDisputes(); }, [fetchDisputes]);
  useEffect(() => { fetchStats(); }, []);

  const handleSearch = () => { setSearchQuery(searchInput); setPage(1); };
  const handleKeyPress = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };

  // Actions
  const handleApprove = async (d: UnifiedDispute) => {
    setActionLoading(true);
    try {
      const res = await apiClient.post(`/admin/unified-disputes/${d.id}/approve`, { type: d.type });
      if (res.data.status === 'success') {
        enqueueSnackbar('اختلاف تأیید شد', { variant: 'success' });
        fetchDisputes(); fetchStats(); setDetailsOpen(false);
      }
    } catch (e: any) {
      enqueueSnackbar(e.response?.data?.message || 'خطا', { variant: 'error' });
    } finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!selectedDispute || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      const res = await apiClient.post(`/admin/unified-disputes/${selectedDispute.id}/reject`, {
        type: selectedDispute.type, reason: rejectReason
      });
      if (res.data.status === 'success') {
        enqueueSnackbar('اختلاف رد شد', { variant: 'success' });
        fetchDisputes(); fetchStats(); setRejectOpen(false); setDetailsOpen(false); setRejectReason('');
      }
    } catch (e: any) {
      enqueueSnackbar(e.response?.data?.message || 'خطا', { variant: 'error' });
    } finally { setActionLoading(false); }
  };

  // Open reassign dialog + load eligible (dispute-handling, on-shift) admins
  const openReassign = async (d: UnifiedDispute) => {
    setReassignDispute(d);
    // Pre-select the current assignee (like the ticket assign dialog) so reassigning
    // is an explicit change, and assigning an unassigned dispute starts blank.
    setSelectedAdminId(d.assignedAdminId || '');
    setReassignOpen(true);
    setEligibleLoading(true);
    try {
      const res = await apiClient.get('/admin/disputes/eligible-admins');
      const data = res.data?.data || res.data;
      setEligibleAdmins(data.admins || []);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در دریافت لیست ادمین‌ها', { variant: 'error' });
      setEligibleAdmins([]);
    } finally {
      setEligibleLoading(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignDispute?.chatId || !selectedAdminId) return;
    setReassigning(true);
    try {
      const res = await apiClient.post(`/admin/disputes/chats/${reassignDispute.chatId}/reassign`, {
        newAdminId: selectedAdminId,
      });
      enqueueSnackbar(res.data?.message || 'اختلاف واگذار شد', { variant: 'success' });
      setReassignOpen(false);
      setReassignDispute(null);
      setSelectedAdminId('');
      fetchDisputes();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در واگذاری اختلاف', { variant: 'error' });
    } finally {
      setReassigning(false);
    }
  };

  const openUnifiedDialog = (d: UnifiedDispute) => {
    setSelectedDispute(d);
    setUnifiedError(null); setUnifiedOpen(true);
  };

  const handleUnifiedAction = async (actionType: ActionType, payload: ActionPayload) => {
    if (!selectedDispute) return;
    setActionLoading(true); setUnifiedError(null);
    try {
      let res;
      switch (actionType) {
        case 'approve':
          res = await apiClient.post(`/admin/unified-disputes/${selectedDispute.id}/approve`, { type: selectedDispute.type });
          break;
        case 'reject':
          res = await apiClient.post(`/admin/unified-disputes/${selectedDispute.id}/reject`, { type: selectedDispute.type, reason: payload.reason });
          break;
        case 'refund':
          res = await apiClient.post(`/admin/unified-disputes/${selectedDispute.id}/refund`, {
            type: selectedDispute.type, amount: payload.refundAmount, reason: payload.reason, applyPenalty: payload.applyPenalty, penaltyPoints: payload.penaltyPoints
          });
          break;
        case 'resolve_buyer':
        case 'resolve_seller':
        case 'resolve_mutual':
          const rtMap: Record<string, string> = { resolve_buyer: 'buyer_favor', resolve_seller: 'seller_favor', resolve_mutual: 'mutual_agreement' };
          res = await apiClient.post(`/admin/unified-disputes/${selectedDispute.id}/resolve`, {
            type: selectedDispute.type, resolution: payload.resolution, resolutionType: rtMap[actionType], tags: payload.tags
          });
          break;
        case 'change_status':
          res = await apiClient.post(`/admin/unified-disputes/${selectedDispute.id}/change-status`, { newStatus: payload.newStatus, reason: payload.reason });
          break;
        case 'cancel_order':
          res = await apiClient.post(`/admin/unified-disputes/${selectedDispute.id}/change-status`, { newStatus: 'cancelled', reason: payload.reason });
          break;
        default: throw new Error('عملیات نامشخص');
      }
      if (res?.data?.status === 'success') {
        const msgs: Record<ActionType, string> = {
          approve: 'تأیید شد', reject: 'رد شد', refund: res.data.message || 'بازپرداخت انجام شد',
          resolve_buyer: 'به نفع خریدار حل شد', resolve_seller: 'به نفع فروشنده حل شد', resolve_mutual: 'با توافق حل شد',
          change_status: 'وضعیت تغییر کرد', cancel_order: 'سفارش لغو شد'
        };
        enqueueSnackbar(msgs[actionType], { variant: 'success' });
        setUnifiedOpen(false); fetchDisputes(); fetchStats();
      }
    } catch (e: any) {
      const msg = e.response?.data?.message || 'خطا';
      setUnifiedError(msg); enqueueSnackbar(msg, { variant: 'error' });
    } finally { setActionLoading(false); }
  };

  const convertToDisputeData = (d: UnifiedDispute): DisputeData => ({
    id: d.id, disputeNumber: d.disputeNumber, type: d.type, status: d.status as any,
    reason: d.reason, reasonLabel: d.reasonLabel, description: d.description,
    parties: { initiator: d.parties.initiator, respondent: d.parties.respondent },
    financial: d.financial, references: d.references, currentItemStatus: 'pending',
    chatId: d.chatId
  });

  const clearFilters = () => { setSearchQuery(''); setSearchInput(''); setStatusFilter('all'); setPriorityFilter('all'); setActiveTab('all'); setPage(1); };
  const hasActiveFilters = searchQuery || statusFilter !== 'all' || priorityFilter !== 'all' || activeTab !== 'all';
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  const statusTabs = [
    { key: 'all', label: 'همه', count: stats?.total || 0 },
    { key: 'pending_admin_approval', label: 'در انتظار', count: stats?.pending || 0 },
    { key: 'approved', label: 'تأیید شده', count: stats?.approved || 0 },
    { key: 'investigating', label: 'بررسی', count: stats?.investigating || 0 },
    { key: 'resolved', label: 'حل شده', count: stats?.resolved || 0 },
    { key: 'rejected', label: 'رد شده', count: stats?.rejected || 0 },
    { key: 'closed', label: 'بسته شده', count: stats?.closed || 0 }
  ];

  return (
    <div className="disputes-page">
      <MyShiftStatusBanner category="dispute" />
      {/* Header */}
      <header className="disputes-header">
        <div>
          <h1 className="disputes-title">مرکز داوری و اختلافات</h1>
          <p className="disputes-subtitle">رسیدگی یکپارچه به اختلافات سفارش و بازرسی</p>
        </div>
        <div className="header-actions">
          <span className="header-count">{total} اختلاف</span>
          <button className="header-btn refresh" onClick={() => { fetchDisputes(); fetchStats(); }} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </header>

      {/* Status filter — clickable KPI cards (counts + status filter merged) */}
      <div className="dc-stat-grid">
        {statusTabs.map(t => {
          const mod = t.key === 'pending_admin_approval' ? 'pending' : t.key;
          return (
            <button
              key={t.key}
              className={`dc-stat ${mod} ${statusFilter === t.key ? 'active' : ''}`}
              onClick={() => { setStatusFilter(t.key as any); setPage(1); }}
              title={`فیلتر: ${t.label}`}
            >
              <span className="dc-stat-count">{t.count}</span>
              <span className="dc-stat-label">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Type Chips */}
      <div className="type-chips">
        <button className={`type-chip ${activeTab === 'all' ? 'active all' : ''}`} onClick={() => { setActiveTab('all'); setPage(1); }}>
          <GavelIcon style={{ fontSize: 14 }} /> همه انواع
        </button>
        <button className={`type-chip ${activeTab === 'order' ? 'active order' : ''}`} onClick={() => { setActiveTab('order'); setPage(1); }}>
          <ShoppingCartIcon style={{ fontSize: 14 }} /> سفارش ({stats?.byType.order || 0})
        </button>
        <button className={`type-chip ${activeTab === 'order_item' ? 'active order_item' : ''}`} onClick={() => { setActiveTab('order_item'); setPage(1); }}>
          <InventoryIcon style={{ fontSize: 14 }} /> محصول ({stats?.byType.order_item || 0})
        </button>
        <button className={`type-chip ${activeTab === 'inspection' ? 'active inspection' : ''}`} onClick={() => { setActiveTab('inspection'); setPage(1); }}>
          <PolicyIcon style={{ fontSize: 14 }} /> بازرسی ({stats?.byType.inspection || 0})
        </button>
      </div>

      {/* Filters */}
      <div className="disputes-filters">
        <div className="filters-row">
          <div className="filter-search-group">
            <input
              className="filter-search"
              placeholder="جستجو شماره، نام، محصول..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button className="filter-search-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
            </button>
          </div>
          <select className="filter-select" value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value as any); setPage(1); }}>
            <option value="all">همه اولویت‌ها</option>
            <option value="urgent">فوری</option>
            <option value="high">بالا</option>
            <option value="medium">متوسط</option>
            <option value="low">پایین</option>
          </select>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchQuery && <span className="filter-tag">جستجو: {searchQuery}<button onClick={() => { setSearchQuery(''); setSearchInput(''); }}>×</button></span>}
            {statusFilter !== 'all' && <span className="filter-tag">وضعیت: {statusLabels[statusFilter]}<button onClick={() => setStatusFilter('all')}>×</button></span>}
            {priorityFilter !== 'all' && <span className="filter-tag">اولویت: {priorityLabels[priorityFilter]}<button onClick={() => setPriorityFilter('all')}>×</button></span>}
            {activeTab !== 'all' && <span className="filter-tag">نوع: {typeLabels[activeTab]}<button onClick={() => setActiveTab('all')}>×</button></span>}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="disputes-table-wrapper">
        {loading ? (
          <div className="disputes-loading"><CircularProgress size={36} /></div>
        ) : disputes.length === 0 ? (
          <div className="disputes-empty">
            <div className="disputes-empty-icon">⚖️</div>
            <p className="disputes-empty-text">اختلافی یافت نشد</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="disputes-table">
              <thead>
                <tr>
                  <th>شماره</th>
                  <th>نوع</th>
                  <th>وضعیت</th>
                  <th>اولویت</th>
                  <th>شاکی</th>
                  <th>طرف مقابل</th>
                  <th>مبلغ</th>
                  <th>مسئول</th>
                  <th>تاریخ</th>
                  <th style={{ width: 150 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {disputes.map(d => (
                  <tr key={d.id} className={d.priority === 'urgent' ? 'row-urgent' : ''}>
                    <td><span className="cell-code">#{d.disputeNumber}</span></td>
                    <td><span className={`tag tag-${d.type}`}>{typeLabels[d.type]}</span></td>
                    <td><span className={`tag tag-${d.status === 'pending_admin_approval' ? 'pending' : d.status}`}>{statusLabels[d.status]}</span></td>
                    <td><span className={`tag tag-${d.priority}`}>{priorityLabels[d.priority]}</span></td>
                    <td>
                      <div className="cell-with-icon">
                        <PersonIcon />
                        <span>{d.parties.initiator.name}</span>
                      </div>
                    </td>
                    <td>
                      {d.parties.respondent ? (
                        <div className="cell-with-icon">
                          <StoreIcon />
                          <span>{d.parties.respondent.name}</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="cell-price">{d.financial ? `${formatPrice(d.financial.amount)} ت` : '-'}</td>
                    <td>
                      {d.assignedAdminName ? (
                        <div className="cell-with-icon"><PersonIcon /><span>{d.assignedAdminName}</span></div>
                      ) : (
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>تخصیص‌نیافته</span>
                      )}
                    </td>
                    <td className="cell-date">{formatRelativeTime(d.createdAt)}</td>
                    <td>
                      <div className="actions-cell">
                        {/* ✅ تمام تعاملات با اختلاف از طریق صفحه داوری انجام می‌شه:
                            «مدیریت» و «جزئیات» هر دو ادمین رو به /arbitration/{id}
                            می‌برن — جایی که چت + تمام اقدامات (تأیید/رد/بازپرداخت/...)
                            یکپارچه نمایش داده می‌شن. dialog قدیمی resolution برای
                            backward-compat نگه داشته شده ولی trigger ندارد. */}
                        <Tooltip title="مدیریت / ورود به صفحه داوری" arrow><button className="action-btn primary" onClick={() => navigate(`/arbitration/${d.chatId || d.id}`)}><GavelIcon /></button></Tooltip>
                        <Tooltip title="جزئیات" arrow><button className="action-btn" onClick={() => navigate(`/arbitration/${d.chatId || d.id}`)}><InfoOutlinedIcon /></button></Tooltip>
                        {d.status === 'pending_admin_approval' && (
                          <>
                            <Tooltip title="تأیید" arrow><button className="action-btn success" onClick={() => handleApprove(d)} disabled={actionLoading}><CheckCircleIcon /></button></Tooltip>
                            <Tooltip title="رد" arrow><button className="action-btn danger" onClick={() => { setSelectedDispute(d); setRejectOpen(true); }} disabled={actionLoading}><CancelIcon /></button></Tooltip>
                          </>
                        )}
                        {['approved', 'investigating'].includes(d.status) && d.chatId && (
                          <Tooltip title="چت داوری" arrow><button className="action-btn" onClick={() => navigate(`/arbitration/${d.chatId}`)}><ChatIcon /></button></Tooltip>
                        )}
                        {d.chatId && (
                          <Tooltip title="تخصیص به ادمین" arrow><button className="action-btn" onClick={() => openReassign(d)}><PersonIcon /></button></Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="disputes-cards">
              {disputes.map(d => (
                <div key={d.id} className={`dc-card ${d.priority === 'urgent' ? 'urgent' : ''}`}>
                  {/* Top: type icon + number/party + status */}
                  <div className="dc-card-top">
                    <span className={`dc-card-ico ${d.type}`}>
                      {d.type === 'order' ? <ShoppingCartIcon fontSize="small" /> : d.type === 'order_item' ? <InventoryIcon fontSize="small" /> : <PolicyIcon fontSize="small" />}
                    </span>
                    <div className="dc-card-id">
                      <span className="dc-card-num">#{d.disputeNumber}</span>
                      <span className="dc-card-party">{d.parties.initiator.name}</span>
                    </div>
                    <span className={`tag tag-${d.status === 'pending_admin_approval' ? 'pending' : d.status}`}>{statusLabels[d.status]}</span>
                  </div>

                  {/* Compact meta line */}
                  <div className="dc-card-meta">
                    <span>{typeLabels[d.type]}</span>
                    {d.parties.respondent && <><i>·</i><span>{d.parties.respondent.name}</span></>}
                    {d.financial && <><i>·</i><span className="dc-amount">{formatPrice(d.financial.amount)} ت</span></>}
                    <i>·</i><span>{formatRelativeTime(d.createdAt)}</span>
                    <i>·</i><span>{d.assignedAdminName ? `مسئول: ${d.assignedAdminName}` : 'تخصیص‌نیافته'}</span>
                  </div>

                  {/* Actions: primary button + compact icon buttons */}
                  <div className="dc-card-acts">
                    <button className="dc-act-main" onClick={() => navigate(`/arbitration/${d.chatId || d.id}`)}><GavelIcon fontSize="small" /> مدیریت</button>
                    <button className="dc-act-ico" title="جزئیات" onClick={() => navigate(`/arbitration/${d.chatId || d.id}`)}><InfoOutlinedIcon fontSize="small" /></button>
                    {d.chatId && ['approved', 'investigating'].includes(d.status) && (
                      <button className="dc-act-ico" title="چت داوری" onClick={() => navigate(`/arbitration/${d.chatId}`)}><ChatIcon fontSize="small" /></button>
                    )}
                    {d.chatId && (
                      <button className="dc-act-ico" title="تخصیص" onClick={() => openReassign(d)}><PersonIcon fontSize="small" /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="disputes-pagination">
              <span className="pagination-info">{startItem} تا {endItem} از {total}</span>
              <div className="pagination-controls">
                <button className="pagination-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronRightIcon fontSize="small" /></button>
                <span className="pagination-text">صفحه {page} از {totalPages}</span>
                <button className="pagination-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}><ChevronLeftIcon fontSize="small" /></button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth className="dispute-dialog" dir="rtl">
        {selectedDispute && (
          <>
            <DialogTitle className="dispute-dialog-title">
              <span>جزئیات اختلاف #{selectedDispute.disputeNumber}</span>
              <button className="dialog-close" onClick={() => setDetailsOpen(false)}><CloseIcon fontSize="small" /></button>
            </DialogTitle>
            <DialogContent className="dispute-dialog-content">
              <div className="details-badges">
                <span className={`tag tag-${selectedDispute.type}`}>{typeLabels[selectedDispute.type]}</span>
                <span className={`tag tag-${selectedDispute.status === 'pending_admin_approval' ? 'pending' : selectedDispute.status}`}>{statusLabels[selectedDispute.status]}</span>
                <span className={`tag tag-${selectedDispute.priority}`}>اولویت: {priorityLabels[selectedDispute.priority]}</span>
              </div>

              <div className="details-grid">
                <div className="details-field">
                  <div className="details-field-label"><PersonIcon /> شاکی</div>
                  <div className="details-field-value">
                    <strong>{selectedDispute.parties.initiator.name}</strong>
                    {selectedDispute.parties.initiator.email && <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4 }}>{selectedDispute.parties.initiator.email}</div>}
                  </div>
                </div>
                {selectedDispute.parties.respondent && (
                  <div className="details-field">
                    <div className="details-field-label"><StoreIcon /> طرف مقابل</div>
                    <div className="details-field-value"><strong>{selectedDispute.parties.respondent.name}</strong></div>
                  </div>
                )}

                <div className="details-field full">
                  <div className="details-field-label">دلیل اختلاف</div>
                  <div className="details-field-value warning">
                    <strong>{selectedDispute.reasonLabel}</strong>
                    <div style={{ marginTop: 6 }}>{selectedDispute.description}</div>
                  </div>
                </div>

                {selectedDispute.references.orderNumber && (
                  <div className="details-field"><div className="details-field-label">شماره سفارش</div><div className="details-field-value">{selectedDispute.references.orderNumber}</div></div>
                )}
                {selectedDispute.references.productTitle && (
                  <div className="details-field"><div className="details-field-label">محصول</div><div className="details-field-value">{selectedDispute.references.productTitle}</div></div>
                )}
                {selectedDispute.references.storeName && (
                  <div className="details-field"><div className="details-field-label">فروشگاه</div><div className="details-field-value">{selectedDispute.references.storeName}</div></div>
                )}

                {selectedDispute.financial && (
                  <div className="details-field full">
                    <div className="details-field-label"><MonetizationOnIcon /> اطلاعات مالی</div>
                    <div className="details-financial-grid">
                      <div className="details-financial-item">
                        <div className="details-financial-label">مبلغ</div>
                        <div className="details-financial-value">{formatPrice(selectedDispute.financial.amount)} ت</div>
                      </div>
                      {selectedDispute.financial.shippingCost && (
                        <div className="details-financial-item">
                          <div className="details-financial-label">هزینه ارسال</div>
                          <div className="details-financial-value">{formatPrice(selectedDispute.financial.shippingCost)} ت</div>
                        </div>
                      )}
                      <div className="details-financial-item highlight">
                        <div className="details-financial-label">قابل بازپرداخت</div>
                        <div className="details-financial-value">{formatPrice(selectedDispute.financial.refundableAmount)} ت</div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="details-field full">
                  <div className="details-field-label"><AccessTimeIcon /> تاریخچه</div>
                  <div className="details-field-value">
                    <div><strong>ایجاد:</strong> {formatDate(selectedDispute.createdAt)}</div>
                    {selectedDispute.approvedAt && <div style={{ color: 'var(--color-success-dark)', marginTop: 6 }}><strong>تأیید:</strong> {formatDate(selectedDispute.approvedAt)}{selectedDispute.adminInfo?.approvedByName && ` توسط ${selectedDispute.adminInfo.approvedByName}`}</div>}
                    {selectedDispute.rejectedAt && <div style={{ color: 'var(--color-error-dark)', marginTop: 6 }}><strong>رد:</strong> {formatDate(selectedDispute.rejectedAt)}{selectedDispute.adminInfo?.rejectedByName && ` توسط ${selectedDispute.adminInfo.rejectedByName}`}</div>}
                    {selectedDispute.resolvedAt && <div style={{ color: 'var(--color-success-dark)', marginTop: 6 }}><strong>حل:</strong> {formatDate(selectedDispute.resolvedAt)}{selectedDispute.adminInfo?.resolvedByName && ` توسط ${selectedDispute.adminInfo.resolvedByName}`}</div>}
                  </div>
                </div>

                {(selectedDispute.adminInfo?.resolution || selectedDispute.adminInfo?.rejectReason || selectedDispute.adminInfo?.refundAmount) && (
                  <div className="details-field full">
                    <div className="details-field-label">جزئیات تصمیم</div>
                    <div className="details-field-value info">
                      {selectedDispute.adminInfo?.resolutionType && (
                        <div style={{ marginBottom: 10 }}>
                          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>نوع حل: </span>
                          <span className={`tag tag-${selectedDispute.adminInfo.resolutionType === 'buyer_favor' ? 'approved' : 'resolved'}`}>
                            {selectedDispute.adminInfo.resolutionType === 'buyer_favor' ? 'به نفع خریدار' : selectedDispute.adminInfo.resolutionType === 'seller_favor' ? 'به نفع فروشنده' : 'توافق طرفین'}
                          </span>
                        </div>
                      )}
                      {selectedDispute.adminInfo?.resolution && <div style={{ marginBottom: 10 }}><strong>نتیجه:</strong> {selectedDispute.adminInfo.resolution}</div>}
                      {selectedDispute.adminInfo?.rejectReason && <div className="details-field-value error" style={{ marginBottom: 10 }}><strong>دلیل رد:</strong> {selectedDispute.adminInfo.rejectReason}</div>}
                      {selectedDispute.adminInfo?.refundAmount && (
                        <div className="details-field-value success" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <MonetizationOnIcon />
                          <div><div style={{ fontSize: 11 }}>مبلغ بازپرداخت</div><strong>{formatPrice(selectedDispute.adminInfo.refundAmount)} تومان</strong></div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
            <div className="dispute-dialog-actions">
              {selectedDispute.chatId && <button className="dialog-btn primary" onClick={() => navigate(`/arbitration/${selectedDispute.chatId}`)}><ChatIcon fontSize="small" /> رفتن به چت</button>}
              <button className="dialog-btn cancel" onClick={() => setDetailsOpen(false)}>بستن</button>
            </div>
          </>
        )}
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth className="dispute-dialog" dir="rtl">
        <DialogTitle className="dispute-dialog-title">
          <span>رد اختلاف #{selectedDispute?.disputeNumber}</span>
          <button className="dialog-close" onClick={() => setRejectOpen(false)}><CloseIcon fontSize="small" /></button>
        </DialogTitle>
        <DialogContent className="dispute-dialog-content">
          <div className="reject-warning">
            <CancelIcon />
            <div className="reject-warning-text">با رد این اختلاف، درخواست کاربر بسته می‌شود.</div>
          </div>
          <div className="form-field">
            <label>دلیل رد <span className="required">*</span></label>
            <textarea className="form-textarea" value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="دلیل رد اختلاف..." />
          </div>
        </DialogContent>
        <div className="dispute-dialog-actions">
          <button className="dialog-btn cancel" onClick={() => setRejectOpen(false)}>انصراف</button>
          <button className="dialog-btn danger" onClick={handleReject} disabled={!rejectReason.trim() || actionLoading}>
            {actionLoading ? <CircularProgress size={18} /> : 'رد اختلاف'}
          </button>
        </div>
      </Dialog>

      {/* Resolution Dialogs */}
      <UnifiedResolutionDialog open={unifiedOpen} onClose={() => setUnifiedOpen(false)} dispute={selectedDispute ? convertToDisputeData(selectedDispute) : null} onAction={handleUnifiedAction} loading={actionLoading} error={unifiedError} />

      {/* Reassign Dialog */}
      <Dialog open={reassignOpen} onClose={() => setReassignOpen(false)} maxWidth="sm" fullWidth className="dispute-dialog" dir="rtl">
        <DialogTitle className="dispute-dialog-title">
          <span>تخصیص اختلاف به ادمین</span>
          <button className="dialog-close" onClick={() => setReassignOpen(false)}><CloseIcon fontSize="small" /></button>
        </DialogTitle>
        <DialogContent className="dispute-dialog-content">
          <p style={{ marginBottom: 6, fontSize: 13 }}>
            مسئول فعلی: <strong>{reassignDispute?.assignedAdminName || 'تخصیص‌نیافته'}</strong>
          </p>
          <p style={{ marginBottom: 12, color: 'var(--color-text-secondary)', fontSize: 13 }}>
            فقط ادمین‌هایی که مسئول رسیدگی به اختلافات هستند و در شیفت قرار دارند نمایش داده می‌شوند.
          </p>
          {eligibleLoading ? (
            <div style={{ textAlign: 'center', padding: 16 }}><CircularProgress size={24} /></div>
          ) : eligibleAdmins.length === 0 ? (
            <div style={{ padding: 12, color: 'var(--color-error-dark)' }}>هیچ ادمین واجد شرایطی یافت نشد.</div>
          ) : (
            <select
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--color-border)' }}
              value={selectedAdminId}
              onChange={e => setSelectedAdminId(e.target.value)}
            >
              <option value="">— انتخاب ادمین —</option>
              {eligibleAdmins.map(a => (
                <option key={a._id} value={a._id}>
                  {a.name}
                  {a.isSuperAdmin ? ' (سوپرادمین)' : ''}
                  {typeof a.currentLoad === 'number' ? ` — بار فعلی: ${a.currentLoad}` : ''}
                  {a.overloaded ? ' — پرظرفیت' : ''}
                </option>
              ))}
            </select>
          )}
        </DialogContent>
        <div className="dispute-dialog-actions">
          <button className="dialog-btn cancel" onClick={() => setReassignOpen(false)}>انصراف</button>
          <button className="dialog-btn primary" onClick={handleReassign} disabled={!selectedAdminId || selectedAdminId === reassignDispute?.assignedAdminId || reassigning}>
            {reassigning ? <CircularProgress size={18} /> : 'تخصیص'}
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default DisputeCenter;
